import { type CollabViewSortType } from '@databases/model/views/view-sort';
import {
  type SortArray,
  type MutableSortArray,
  type Sort,
  type MutableSort,
} from '@databases/model/views/view-sort';
import * as Y from 'yjs';

import { type LazyValue } from '../helpers/lazy-value';
import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from '../helpers/types';

const SortProperties = {
  ascending: 'a',
  fieldId: 'fi',
  customOrder: 'co',
};

export class YSort
  extends SynchronizedRecord
  implements DisposableElement, WrappedYType, Sort, MutableSort
{
  static mapper(value: unknown): YSort | undefined {
    if (value instanceof Y.Map) {
      return new YSort(value);
    }
  }

  get ascending(): boolean {
    return this.getBoolean(SortProperties.ascending, false);
  }

  set ascending(ascending: boolean) {
    this.set(SortProperties.ascending, ascending);
  }

  get fieldId(): string {
    return this.getString(SortProperties.fieldId, '');
  }

  set fieldId(fieldId: string) {
    this.set(SortProperties.fieldId, fieldId);
  }

  get customOrder(): boolean | undefined {
    const value = this.getBoolean(SortProperties.customOrder, undefined);
    return value === undefined ? undefined : value;
  }

  set customOrder(customOrder: boolean | undefined) {
    if (customOrder === undefined) {
      this.data.delete(SortProperties.customOrder);
    } else {
      this.set(SortProperties.customOrder, customOrder);
    }
  }

  toJSON(): CollabViewSortType {
    const result: CollabViewSortType = {
      ascending: this.ascending,
      fieldId: this.fieldId,
    };
    const customOrder = this.customOrder;
    if (customOrder !== undefined) {
      result.customOrder = customOrder;
    }
    return result;
  }

  static fromJSON(json: CollabViewSortType): YSort {
    const entries: [string, unknown][] = [
      [SortProperties.ascending, json.ascending],
      [SortProperties.fieldId, json.fieldId],
    ];
    if (json.customOrder !== undefined) {
      entries.push([SortProperties.customOrder, json.customOrder]);
    }
    return new YSort(new Y.Map(entries));
  }
}

export class YSortArray
  implements DisposableElement, WrappedYType, SortArray, MutableSortArray
{
  private sortArray: SynchronizedArray<YSort>;

  constructor(input: Y.Array<unknown> | LazyValue<Y.Array<unknown>>) {
    this.sortArray = new SynchronizedArray(input, YSort.mapper);
  }

  insertSort(index: number, sort: CollabViewSortType): void {
    this.sortArray.insert(index, [YSort.fromJSON(sort)]);
  }

  appendSort(sort: CollabViewSortType): void {
    this.sortArray.push([YSort.fromJSON(sort)]);
  }

  deleteSortForFieldId(fieldId: string): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    this.sortArray.delete(index);
  }

  deleteSortForFieldIds(fieldIds: string[]): void {
    fieldIds.forEach((fieldId) => this.deleteSortForFieldId(fieldId));
  }

  clearAll(): void {
    while (this.sortArray.length > 0) {
      this.sortArray.delete(0);
    }
  }

  updateSortForFieldId(fieldId: string, ascending: boolean, customOrder?: boolean): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    const sort = this.sortArray.get(index);
    sort.ascending = ascending;
    sort.customOrder = customOrder;
  }

  reorderSortForFieldId(fieldId: string, newIndex: number): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    const sort = this.sortArray.get(index).toJSON();
    this.sortArray.delete(index);
    this.sortArray.insert(newIndex, [YSort.fromJSON(sort)]);
  }

  unwrap(): Y.AbstractType<string> | undefined {
    return this.sortArray.unwrap();
  }

  dispose(): void {
    this.sortArray.dispose();
  }

  get length(): number {
    return this.sortArray.length;
  }

  toArray(): CollabViewSortType[] {
    return this.sortArray.toArray().map((ySort) => ySort.toJSON());
  }

  static fromJSON(json: CollabViewSortType[]): YSortArray {
    return new YSortArray(
      Y.Array.from<any>(json.map((sort) => YSort.fromJSON(sort).unwrap()))
    );
  }
}
